/**
 * walletCredits — pure, import-free helpers for the SMS credit wallet.
 *
 * Kept side-effect-free so they unit-test directly (mirrored in
 * cypress/node-tests/wallet-store.test.js — KEEP IN SYNC) and so the store
 * mutation (wallet/applyOptimisticDraw) and the pill's colour logic share ONE
 * implementation instead of drifting.
 *
 * Data contract (backend-written): siteWallet/{siteID} =
 *   { creditsAvailable, freeAllowance, freeUsed, freeRemainingCredits,
 *     paidBalanceUsd, paidCredits, creditPriceUsd, resetDate, ... }
 *   creditsAvailable = freeRemainingCredits + paidCredits  (the ONE glance number).
 */

// Colour thresholds for the balance pill / composer line, in credits.
export const LOW_BALANCE_CREDITS = 200;       // amber below this
export const CRITICAL_BALANCE_CREDITS = 20;   // red at/below this

/**
 * Draw `credits` from the wallet — free allowance FIRST, then purchased credits,
 * clamping every field at 0. Pure: returns the next
 * { freeRemainingCredits, paidCredits, creditsAvailable } without mutating input.
 *
 * Powers the OPTIMISTIC local decrement when the current user fires a send:
 * instant feedback that the next Firestore snapshot reconciles.
 */
export function drawCredits(wallet, credits) {
  const free = Math.max(0, Number(wallet && wallet.freeRemainingCredits) || 0);
  const paid = Math.max(0, Number(wallet && wallet.paidCredits) || 0);
  const draw = Math.max(0, Number(credits) || 0);

  const fromFree = Math.min(free, draw);            // spend free allowance first
  const fromPaid = Math.min(paid, draw - fromFree); // then purchased credits

  const freeRemainingCredits = free - fromFree;
  const paidCredits = paid - fromPaid;
  return {
    freeRemainingCredits,
    paidCredits,
    creditsAvailable: freeRemainingCredits + paidCredits,
  };
}

/**
 * Colour state for the pill / composer line from the glanceable numbers:
 *   'danger' — effectively empty (<= CRITICAL, or no free AND no paid)
 *   'warn'   — running low (< LOW_BALANCE_CREDITS)
 *   'ok'     — healthy
 */
export function balanceTone(creditsAvailable, freeRemainingCredits, paidCredits) {
  const c = Math.max(0, Number(creditsAvailable) || 0);
  const free = Math.max(0, Number(freeRemainingCredits) || 0);
  const paid = Math.max(0, Number(paidCredits) || 0);
  if (c <= CRITICAL_BALANCE_CREDITS || (free === 0 && paid === 0)) return 'danger';
  if (c < LOW_BALANCE_CREDITS) return 'warn';
  return 'ok';
}

// Flat credits charged for ONE outbound WhatsApp message — mirrors the layer's
// smsSegments.WHATSAPP_CREDITS. A WhatsApp send draws 1 credit from the shared SMS pool,
// independent of length or media (Twilio bills WhatsApp per-message, not per-segment).
export const WHATSAPP_CREDITS = 1;

/**
 * Credits to OPTIMISTICALLY draw for one outbound message the current user just sent,
 * keyed by the channel it goes out on — so the header pill drops the instant Send is
 * pressed (reconciled by the next siteWallet Firestore snapshot). Mirrors the backend's
 * per-channel billing in smsEngine.sendSms:
 *   - 'whatsapp' → flat WHATSAPP_CREDITS (1), independent of length / media
 *   - 'sms'      → the SMS guidance's billableCredits (segment count; MMS = 3; Puerto Rico = 5)
 *   - anything else (in-house 'chat', unknown) → 0 (not billed)
 * Never returns a negative or non-finite value. `smsGuidance` is the object from
 * utils/smsGuidance.getSmsGuidance (only consulted for the 'sms' channel).
 */
export function optimisticDrawCredits(channel, smsGuidance) {
  if (channel === 'whatsapp') return WHATSAPP_CREDITS;
  if (channel === 'sms') {
    const c = Number(smsGuidance && smsGuidance.billableCredits);
    return Number.isFinite(c) && c > 0 ? c : 0;
  }
  return 0;
}
